/**
 * MCP Simulation Tool Handlers
 *
 * Implementations for simulation observability and control tools.
 * These handlers interact with the simulation store to provide
 * real-time access to simulation status, logs, and results.
 */

import type { MCPToolResult } from '../types';
import type { ConsoleLine, SimulationPhase, TimeEstimate } from '../../simulation/progress';
import type { OutputVariable, TimeSeries } from '../../simulation/parsers/types';
import type { SimulationInput, SimulationOutput, SimulationStatus } from '../../simulation/simulationStore';
import { formatDuration } from '../../simulation/progress';

/**
 * Context provided to simulation handlers
 */
export interface SimulationHandlerContext {
  // Status
  getStatus: () => SimulationStatus;
  getPhase: () => SimulationPhase;
  getProgress: () => number;
  getProgressMessage: () => string;
  getError: () => string | null;
  getTimeEstimate: () => TimeEstimate;

  // Console
  getConsoleLines: () => ConsoleLine[];

  // Results
  getOutput: () => SimulationOutput | null;
  getAvailableVariables: () => OutputVariable[];
  getTimeSeries: (variableId: number) => TimeSeries | undefined;

  // WASM status
  isWasmLoaded: () => boolean;
  isMockMode: () => boolean;

  // Control
  startSimulation: () => Promise<void>;
  cancelSimulation: () => void;
  forceCancel: () => void;
  setInput: (input: SimulationInput) => void;
  loadWasm: () => Promise<void>;

  // Input status
  hasInput: () => boolean;

  // IDF content access (for prepare_simulation)
  getIdfContent: () => string | null;
  getIdfName: () => string | null;
  getEpwContent: () => string | null;
  getEpwName: () => string | null;
}

/**
 * Handle get_simulation_status tool
 */
export function handleGetSimulationStatus(
  _args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const status = ctx.getStatus();
  const phase = ctx.getPhase();
  const progress = ctx.getProgress();
  const progressMessage = ctx.getProgressMessage();
  const error = ctx.getError();
  const timeEstimate = ctx.getTimeEstimate();
  const wasmLoaded = ctx.isWasmLoaded();
  const mockMode = ctx.isMockMode();
  const hasInput = ctx.hasInput();

  return {
    success: true,
    content: {
      status,
      phase,
      progress,
      progressMessage,
      error,
      timeEstimate: {
        elapsed: timeEstimate.elapsed,
        elapsedFormatted: formatDuration(timeEstimate.elapsed),
        remaining: timeEstimate.remaining,
        remainingFormatted: timeEstimate.remaining !== null ? formatDuration(timeEstimate.remaining) : null,
        total: timeEstimate.total,
        totalFormatted: timeEstimate.total !== null ? formatDuration(timeEstimate.total) : null,
      },
      wasmLoaded,
      mockMode,
      hasInput,
      canRun: hasInput && (status === 'idle' || status === 'ready' || status === 'complete' || status === 'error'),
      isRunning: status === 'running',
    },
  };
}

/**
 * Handle get_simulation_logs tool
 */
export function handleGetSimulationLogs(
  args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const filter = (args['filter'] as string) || 'all';
  const limit = Math.min((args['limit'] as number) || 100, 1000);
  const offset = (args['offset'] as number) || 0;

  let lines = ctx.getConsoleLines();

  // Apply filter
  if (filter === 'problems') {
    lines = lines.filter(
      (l) => l.type === 'warning' || l.type === 'severe' || l.type === 'fatal'
    );
  } else if (filter !== 'all') {
    lines = lines.filter((l) => l.type === filter);
  }

  // Apply pagination (from the end, most recent first)
  const totalCount = lines.length;
  const startIdx = Math.max(0, totalCount - offset - limit);
  const endIdx = Math.max(0, totalCount - offset);
  const paginatedLines = lines.slice(startIdx, endIdx);

  return {
    success: true,
    content: {
      totalLines: totalCount,
      returnedLines: paginatedLines.length,
      offset,
      filter,
      lines: paginatedLines.map((l) => ({
        text: l.text,
        type: l.type,
        timestamp: l.timestamp,
        timeFormatted: new Date(l.timestamp).toISOString(),
      })),
    },
  };
}

/**
 * Handle get_simulation_errors tool
 */
export function handleGetSimulationErrors(
  args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const output = ctx.getOutput();

  if (!output) {
    return {
      success: false,
      error: 'No simulation results available. Run a simulation first.',
    };
  }

  if (!output.err) {
    return {
      success: true,
      content: {
        message: 'No error file available',
        entries: [],
        summary: {
          warningCount: 0,
          severeCount: 0,
          fatalCount: 0,
          simulationSuccessful: true,
        },
      },
    };
  }

  const severity = (args['severity'] as string) || 'all';
  const limit = (args['limit'] as number) || 50;

  let entries = output.err.entries;

  // Apply severity filter
  if (severity !== 'all') {
    entries = entries.filter((e) => e.severity === severity);
  }

  // Apply limit
  const limitedEntries = entries.slice(0, limit);

  return {
    success: true,
    content: {
      totalEntries: entries.length,
      returnedEntries: limitedEntries.length,
      severityFilter: severity,
      entries: limitedEntries.map((e) => ({
        severity: e.severity,
        message: e.message,
        context: e.context,
        count: e.count,
      })),
      summary: {
        warningCount: output.err.warningCount,
        severeCount: output.err.severeCount,
        fatalCount: output.err.fatalCount,
        simulationSuccessful: output.err.simulationSuccessful,
      },
    },
  };
}

/**
 * Handle get_simulation_results tool
 */
export function handleGetSimulationResults(
  _args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const status = ctx.getStatus();
  const output = ctx.getOutput();
  const availableVariables = ctx.getAvailableVariables();

  if (status !== 'complete' && status !== 'error') {
    return {
      success: false,
      error: `Simulation not complete. Current status: ${status}`,
    };
  }

  if (!output) {
    return {
      success: false,
      error: 'No simulation output available',
    };
  }

  // Categorize variables by type
  const variablesByType: Record<string, number> = {};
  for (const v of availableVariables) {
    const category = v.variableName.split(' ')[0] || 'Other';
    variablesByType[category] = (variablesByType[category] || 0) + 1;
  }

  return {
    success: true,
    content: {
      status,
      hasEsoData: !!output.eso,
      hasMtrData: !!output.mtr,
      hasErrData: !!output.err,
      hasHtmlReport: !!output.html,
      hasCsvData: !!output.csv,
      variableCount: availableVariables.length,
      variablesByType,
      errors: output.err
        ? {
            warningCount: output.err.warningCount,
            severeCount: output.err.severeCount,
            fatalCount: output.err.fatalCount,
            simulationSuccessful: output.err.simulationSuccessful,
          }
        : null,
      programVersion: output.eso?.programVersion || 'Unknown',
    },
  };
}

/**
 * Handle get_output_variables tool
 */
export function handleGetOutputVariables(
  args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const availableVariables = ctx.getAvailableVariables();

  if (availableVariables.length === 0) {
    return {
      success: false,
      error: 'No output variables available. Run a simulation first.',
    };
  }

  const searchArg = args['search'] as string | undefined;
  const keyValueArg = args['keyValue'] as string | undefined;
  const search = searchArg?.toLowerCase();
  const keyValue = keyValueArg?.toLowerCase();
  const limit = (args['limit'] as number) || 50;

  let variables = availableVariables;

  // Apply search filter
  if (search) {
    variables = variables.filter(
      (v) =>
        v.variableName.toLowerCase().includes(search) ||
        v.keyValue.toLowerCase().includes(search)
    );
  }

  // Apply key value filter
  if (keyValue) {
    variables = variables.filter((v) =>
      v.keyValue.toLowerCase().includes(keyValue)
    );
  }

  // Apply limit
  const limitedVariables = variables.slice(0, limit);

  return {
    success: true,
    content: {
      totalVariables: variables.length,
      returnedVariables: limitedVariables.length,
      searchFilter: search || null,
      keyValueFilter: keyValue || null,
      variables: limitedVariables.map((v) => ({
        id: v.id,
        variableName: v.variableName,
        keyValue: v.keyValue,
        units: v.units,
        reportFrequency: v.reportFrequency,
      })),
    },
  };
}

/**
 * Handle get_time_series_data tool
 */
export function handleGetTimeSeriesData(
  args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const variableIdArg = args['variableId'] as number | undefined;
  const startIndex = (args['startIndex'] as number) || 0;
  const limit = Math.min((args['limit'] as number) || 100, 1000);

  if (variableIdArg === undefined) {
    return {
      success: false,
      error: 'variableId is required',
    };
  }

  const variableId = variableIdArg;

  const timeSeries = ctx.getTimeSeries(variableId);

  if (!timeSeries) {
    return {
      success: false,
      error: `No time series data found for variable ID ${String(variableId)}`,
    };
  }

  const data = timeSeries.data.slice(startIndex, startIndex + limit);

  // Calculate basic statistics
  const values = data.map((d) => d.value);
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = values.length > 0 ? sum / values.length : 0;
  const min = values.length > 0 ? Math.min(...values) : 0;
  const max = values.length > 0 ? Math.max(...values) : 0;

  return {
    success: true,
    content: {
      variableId,
      variable: {
        name: timeSeries.variable.variableName,
        keyValue: timeSeries.variable.keyValue,
        units: timeSeries.variable.units,
        reportFrequency: timeSeries.variable.reportFrequency,
      },
      totalDataPoints: timeSeries.data.length,
      returnedDataPoints: data.length,
      startIndex,
      statistics: {
        min,
        max,
        average: avg,
        sum,
      },
      data: data.map((d) => ({
        timestamp: {
          month: d.timestamp.month,
          day: d.timestamp.day,
          hour: d.timestamp.hour,
          minute: d.timestamp.minute,
          dayOfSimulation: d.timestamp.dayOfSimulation,
        },
        value: d.value,
      })),
    },
  };
}

/**
 * Handle prepare_simulation tool
 * Loads the current IDF model into the simulation engine
 */
export async function handlePrepareSimulation(
  _args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): Promise<MCPToolResult> {
  const idfContent = ctx.getIdfContent();
  const idfName = ctx.getIdfName();

  if (!idfContent) {
    return {
      success: false,
      error: 'No IDF model is currently loaded in the editor. Open or create an IDF file first.',
    };
  }

  // Load WASM if not already loaded
  const wasmLoaded = ctx.isWasmLoaded();
  if (!wasmLoaded) {
    try {
      await ctx.loadWasm();
    } catch (err) {
      return {
        success: false,
        error: `Failed to load simulation engine: ${err instanceof Error ? err.message : 'Unknown error'}`,
      };
    }
  }

  // Prepare the simulation input
  const epwContent = ctx.getEpwContent();
  const epwName = ctx.getEpwName();
  const input: SimulationInput = {
    idf: idfContent,
    idfName: idfName || 'model.idf',
    ...(epwContent ? { epw: epwContent } : {}),
    ...(epwName ? { epwName } : {}),
  };

  // Load the model into the simulation engine
  ctx.setInput(input);

  const hasWeather = !!input.epw;

  return {
    success: true,
    content: {
      message: 'Model loaded into simulation engine. You can now use start_simulation to run the simulation.',
      idfName: input.idfName,
      hasWeatherFile: hasWeather,
      weatherFileName: input.epwName || null,
      warning: hasWeather
        ? null
        : 'No weather file loaded. Most simulations require a weather file (EPW).',
      suggestedAction: hasWeather
        ? null
        : {
            description: 'Select a weather file before running the simulation',
            actionLink: '[Select Weather File](/action/select-weather)',
          },
    },
  };
}

/**
 * Handle start_simulation tool
 */
export function handleStartSimulation(
  _args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const status = ctx.getStatus();
  const hasInput = ctx.hasInput();

  if (!hasInput) {
    return {
      success: false,
      error: 'No IDF model loaded. Call prepare_simulation first to load the model into the simulation engine.',
      content: {
        suggestedAction: {
          description: 'Go to the simulation page to configure and run simulation',
          actionLink: '[Go to Simulation](/action/go-to-simulation)',
        },
      },
    };
  }

  if (status === 'running') {
    return {
      success: false,
      error: 'Simulation is already running. Use cancel_simulation to stop it first.',
    };
  }

  // Start the simulation asynchronously
  ctx.startSimulation().catch((err: unknown) => {
    console.error('Simulation start error:', err);
  });

  return {
    success: true,
    content: {
      message: 'Simulation started. Use get_simulation_status or get_simulation_logs to monitor progress.',
      previousStatus: status,
    },
  };
}

/**
 * Handle cancel_simulation tool
 */
export function handleCancelSimulation(
  args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const status = ctx.getStatus();
  const forceCancel = args['forceCancel'] as boolean;

  if (status !== 'running') {
    return {
      success: false,
      error: `Simulation is not running. Current status: ${status}`,
    };
  }

  if (forceCancel) {
    ctx.forceCancel();
    return {
      success: true,
      content: {
        message: 'Simulation force cancelled. Worker terminated. WASM will need to reload for next simulation.',
        forceCancel: true,
      },
    };
  }

  ctx.cancelSimulation();
  return {
    success: true,
    content: {
      message: 'Simulation cancel requested. If simulation does not stop, use forceCancel=true.',
      forceCancel: false,
    },
  };
}

/**
 * Handle get_simulation_progress tool
 */
export function handleGetSimulationProgress(
  _args: Record<string, unknown>,
  ctx: SimulationHandlerContext
): MCPToolResult {
  const status = ctx.getStatus();
  const phase = ctx.getPhase();
  const progress = ctx.getProgress();
  const progressMessage = ctx.getProgressMessage();
  const timeEstimate = ctx.getTimeEstimate();
  const consoleLines = ctx.getConsoleLines();

  // Get recent problems from console
  const recentProblems = consoleLines
    .filter((l) => l.type === 'warning' || l.type === 'severe' || l.type === 'fatal')
    .slice(-5)
    .map((l) => ({
      type: l.type,
      text: l.text,
    }));

  // Phase descriptions
  const phaseDescriptions: Record<SimulationPhase, string> = {
    initializing: 'Processing input files and initializing simulation',
    warmup: 'Running warmup periods to establish initial conditions',
    sizing: 'Calculating zone, system, and plant sizing',
    simulation: 'Running the main simulation',
    postprocess: 'Writing output files and generating reports',
    complete: 'Simulation completed successfully',
    error: 'Simulation encountered an error',
  };

  return {
    success: true,
    content: {
      status,
      phase,
      phaseDescription: phaseDescriptions[phase],
      progress,
      progressMessage,
      timeEstimate: {
        elapsed: timeEstimate.elapsed,
        elapsedFormatted: formatDuration(timeEstimate.elapsed),
        remaining: timeEstimate.remaining,
        remainingFormatted: timeEstimate.remaining !== null ? formatDuration(timeEstimate.remaining) : null,
        percentComplete: progress,
      },
      consoleLineCount: consoleLines.length,
      recentProblems,
    },
  };
}

/**
 * All simulation handlers (sync)
 */
export const simulationHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: SimulationHandlerContext) => MCPToolResult
> = {
  get_simulation_status: handleGetSimulationStatus,
  get_simulation_logs: handleGetSimulationLogs,
  get_simulation_errors: handleGetSimulationErrors,
  get_simulation_results: handleGetSimulationResults,
  get_output_variables: handleGetOutputVariables,
  get_time_series_data: handleGetTimeSeriesData,
  start_simulation: handleStartSimulation,
  cancel_simulation: handleCancelSimulation,
  get_simulation_progress: handleGetSimulationProgress,
};

/**
 * Async simulation handlers (require await)
 */
export const asyncSimulationHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: SimulationHandlerContext) => Promise<MCPToolResult>
> = {
  prepare_simulation: handlePrepareSimulation,
};

/**
 * Check if a tool is a simulation control operation (write-like)
 */
export function isSimulationControlTool(toolName: string): boolean {
  return toolName === 'start_simulation' || toolName === 'cancel_simulation' || toolName === 'prepare_simulation';
}
